import { z } from 'zod';
import { DISPLAYED_REQUEST_STATUSES, estimateSchema, impactSchema, prioritySchema, releaseNoteItemTypeSchema, requestTypeSchema, versionStateSchema, } from './domain.js';
/**
 * Contracts of the product space (screens 06 to 09, 13 and 15). Every route
 * under /api/product is refused to anyone but a PO by the API; screen 06 is
 * also readable by a Référent, for its own team only, through
 * /api/team-priorities.
 */
const page = z.coerce.number().int().min(1).default(1);
// ---------------------------------------------------------------------------
// 07 · File de qualification
// ---------------------------------------------------------------------------
/** « À qualifier » = Soumise + Qualification en cours. */
export const QUEUE_STATUS_FILTERS = ['to_qualify', 'submitted', 'qualifying'];
export const QUEUE_PAGE_SIZES = [6, 20, 50];
/** Product filter value for requests without product (« À attribuer », ADR-003). */
export const NO_PRODUCT_FILTER = 'none';
export const queueQuerySchema = z
    .object({
    q: z.string().trim().max(200).optional(),
    productId: z.union([z.uuid(), z.literal(NO_PRODUCT_FILTER)]).optional(),
    type: requestTypeSchema.optional(),
    status: z.enum(QUEUE_STATUS_FILTERS).default('to_qualify'),
    page,
    pageSize: z.coerce
        .number()
        .int()
        .refine((n) => QUEUE_PAGE_SIZES.includes(n))
        .default(6),
})
    .strict();
const optionalNullable = (schema) => schema.nullable().optional();
/**
 * « Enregistrer la qualification ». Strict: only these keys. Refuse and
 * duplicate are separate actions; `refused` / `duplicate` are not accepted here.
 */
export const qualificationUpdateSchema = z
    .object({
    productId: z.uuid().optional(),
    /** Bug, Évolution, Aide / accès, corrected by the PO. */
    type: requestTypeSchema.optional(),
    status: z.enum(DISPLAYED_REQUEST_STATUSES).optional(),
    priority: optionalNullable(prioritySchema),
    impactRetained: optionalNullable(impactSchema),
    estimate: optionalNullable(estimateSchema),
    targetVersionId: optionalNullable(z.uuid()),
    plannedDeliveryDate: optionalNullable(z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date invalide.')),
    ownerId: optionalNullable(z.uuid()),
})
    .strict();
/** Message au demandeur / Note interne. `expectsReply` is dropped for an internal note. */
export const teamMessageSchema = z
    .object({
    body: z.string().trim().min(1, 'Le message est vide.').max(20000),
    visibility: z.enum(['public', 'internal']),
    expectsReply: z.boolean().default(false),
})
    .strict();
/** ADR-005 §3: the reason is mandatory and sent to the requester as a public message. */
export const refusalSchema = z
    .object({ reason: z.string().trim().min(1, 'Le motif du refus est obligatoire.').max(5000) })
    .strict();
/** « Créer l'item » or « Affecter » an existing Monday item, by its link or its number (ADR-015). */
export const mondayLinkSchema = z.discriminatedUnion('mode', [
    z.object({ mode: z.literal('create') }).strict(),
    z.object({ mode: z.literal('assign'), itemId: z.string().trim().min(1, 'Donnez le lien ou le numéro de l’item.').max(300) }).strict(),
]);
export const duplicateMarkSchema = z.object({ originalId: z.uuid() }).strict();
export const jiraLinkSchema = z.discriminatedUnion('mode', [
    z
        .object({
        mode: z.literal('create'),
        /** Existing epic of the project (screen 08); the portal never creates one. */
        epicKey: z
            .string()
            .trim()
            .regex(/^[A-Za-z][A-Za-z0-9_]*-\d+$/, 'Clé d’épic invalide.')
            .nullable()
            .optional(),
    })
        .strict(),
    z
        .object({
        mode: z.literal('assign'),
        jiraKey: z
            .string()
            .trim()
            .regex(/^[A-Za-z][A-Za-z0-9_]*-\d+$/, 'Clé de ticket invalide, ex. DP-1840.'),
    })
        .strict(),
]);
// ---------------------------------------------------------------------------
// 15 · Saisir pour un collaborateur
// ---------------------------------------------------------------------------
export const peopleQuerySchema = z.object({ q: z.string().trim().max(100).optional() }).strict();
// ---------------------------------------------------------------------------
// 06 · Priorisation par équipe
// ---------------------------------------------------------------------------
export const TEAM_PRIORITY_FILTERS = ['to_prioritize', 'open', 'qualified', 'delivered'];
export const teamPrioritiesQuerySchema = z
    .object({
    teamId: z.uuid().optional(),
    filter: z.enum(TEAM_PRIORITY_FILTERS).default('to_prioritize'),
    productId: z.uuid().optional(),
})
    .strict();
/** « Envoyer ma priorisation »: the team's rows in the chosen order. Writes the DRAFT order only. */
export const teamPrioritySaveSchema = z.object({ requestIds: z.array(z.uuid()).min(1).max(500) }).strict();
// ---------------------------------------------------------------------------
// 09 · Backlog & arbitrage
// ---------------------------------------------------------------------------
export const backlogQuerySchema = z.object({ productId: z.uuid().optional() }).strict();
export const draftOrderSchema = z.object({ requestIds: z.array(z.uuid()).max(1000) }).strict();
export const releaseNoteSaveSchema = z
    .object({
    title: z.string().trim().min(1, 'Le titre est obligatoire.').max(300),
    summary: z.string().trim().max(5000).default(''),
    goodToKnow: z.string().trim().max(5000).default(''),
    items: z
        .array(z
        .object({
        type: releaseNoteItemTypeSchema,
        title: z.string().trim().min(1, 'Chaque item a un titre.').max(300),
        body: z.string().trim().max(5000).default(''),
        requestId: z.uuid().nullable().default(null),
    })
        .strict())
        .max(200),
    closedRequestIds: z.array(z.uuid()).max(500),
})
    .strict();
// Versions, managed on screen 13 (ADR-012): created, dated, moved from state to state by hand (ADR-005 §2), deleted while unused.
export const versionCreateSchema = z
    .object({
    number: z.string().trim().min(1, 'Le numéro est obligatoire.').max(40),
    plannedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
})
    .strict();
export const versionUpdateSchema = z
    .object({
    plannedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    deliveredOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
    state: versionStateSchema.optional(),
})
    .strict();
//# sourceMappingURL=product-space.js.map